import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { T } from './theme';
import MissesPanel from './MissesPanel';
import { Pencil, Stethoscope, Pill, Clock, HeartPulse, FlaskConical, FolderOpen, Printer, Shield, PenLine, Building2, ClipboardList } from 'lucide-react';

const API = 'https://tcio-api.onrender.com';
const card = { background: '#fff', border: `1px solid ${T.b100}`, borderRadius: 10 };

const fmtTime = (d) => new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' });
const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

function Tile({ icon, title, main, sub, tone, onClick }) {
  const tones = { ok: [T.ink, '#fff'], warn: ['#8d6e00', '#fff8e1'], bad: ['#b30000', '#fdecea'] };
  const [c, bg] = tones[tone] || tones.ok;
  return (
    <div onClick={onClick} style={{ ...card, minWidth: 150, padding: '11px 14px', cursor: onClick ? 'pointer' : 'default',
      background: tone && tone !== 'ok' ? bg : '#fff', flex: '0 0 auto' }}>
      <div style={{ fontSize: 11, color: T.inkSoft, fontWeight: 700, marginBottom: 3 }}>{icon} {title}</div>
      <div style={{ fontSize: 14.5, fontWeight: 800, color: c, lineHeight: 1.25 }}>{main}</div>
      {sub && <div style={{ fontSize: 11.5, color: T.inkSoft, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export function CareToday({ patientId, episodeId, onGoTab, snapshot }) {
  const [round, setRound] = useState(null);
  const [vitals, setVitals] = useState(null);
  const [mar, setMar] = useState(null);
  const [ov, setOv] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    if (!episodeId) return;
    let live = true;
    (async () => {
      const get = async (url, params) => { try { return (await axios.get(url, { params })).data; } catch { return null; } };
      const [r, v, m, o] = await Promise.all([
        get(`${API}/api/rounds/board-episode`, { episodeId }),
        get(`${API}/api/emr/vitals/next`, { episodeId }),
        patientId ? get(`${API}/api/emr/patients/${patientId}/mar`, { date: new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }), scope_episode: episodeId }) : null,
        get(`${API}/api/emr/overview-cards`, { patientId, episodeId }),
      ]);
      if (!live) return;
      setRound(r && (r.data || r)); setVitals(v && (v.data || v)); setMar(m); setOv(o);
    })();
    return () => { live = false; };
  }, [patientId, episodeId]);

  const tiles = [];
  if (round && round.interval_minutes) {
    const od = round.overdue_minutes != null;
    tiles.push(<Tile key="round" icon={<Clock size={13} strokeWidth={2} />} title="NEXT ROUND"
      main={od ? `OVERDUE +${round.overdue_minutes}m` : round.due_now ? 'DUE NOW' : (round.next_due ? fmtTime(round.next_due) : '—')}
      sub={round.level_code || `Q${round.interval_minutes}`}
      tone={od ? 'bad' : round.due_now ? 'warn' : 'ok'} onClick={() => onGoTab && onGoTab('bht')} />);
  }
  if (vitals && (vitals.next_due || vitals.due_now || vitals.overdue_minutes != null)) {
    const od = vitals.overdue_minutes != null;
    tiles.push(<Tile key="vitals" icon={<HeartPulse size={13} strokeWidth={2} />} title="VITALS"
      main={od ? `OVERDUE +${vitals.overdue_minutes}m` : vitals.due_now ? 'DUE NOW' : (vitals.next_due ? fmtTime(vitals.next_due) : '—')}
      sub={vitals.cadence_label || null}
      tone={od ? 'bad' : vitals.due_now ? 'warn' : 'ok'} onClick={() => onGoTab && onGoTab('bht')} />);
  }
  if (mar && Array.isArray(mar.slots) && mar.slots.length) {
    const nowHM = new Date().toLocaleTimeString('en-GB', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit' });
    const remaining = mar.slots.filter(s => !s.entry && String(s.time || '') >= nowHM);
    const overdue = mar.slots.filter(s => !s.entry && String(s.time || '') < nowHM);
    tiles.push(<Tile key="mar" icon={<Pill size={13} strokeWidth={2} />} title="MEDICATIONS"
      main={overdue.length ? `${overdue.length} PAST DUE` : remaining.length ? `Next ${remaining[0].time}` : 'All given'}
      sub={remaining.length ? `${remaining.length} remaining today` : null}
      tone={overdue.length ? 'bad' : 'ok'} onClick={() => onGoTab && onGoTab('mar')} />);
  }
  if (ov && ov.ur) {
    const d = ov.ur.days_remaining;
    tiles.push(<Tile key="ur" icon={<Shield size={13} strokeWidth={2} />} title="UR AUTH"
      main={ov.ur.date_to ? `Thru ${fmtDate(ov.ur.date_to)}` : (ov.ur.loc || 'Active')}
      sub={d != null ? (d < 0 ? `Expired ${-d}d ago` : `${d} days left`) : null}
      tone={d != null && d < 0 ? 'bad' : d != null && d <= 2 ? 'warn' : 'ok'} />);
  }
  const brief = (ov && ov.brief) || [];
  const atRisk = (ov && ov.at_risk) || [];
  const ruleChecks = (ov && ov.rule_checks) || [];
  if (!snapshot && !tiles.length && !brief.length && !atRisk.length && !ruleChecks.length) return null;
  const briefIcon = { med: <Pill size={12} strokeWidth={2} />, roi: <PenLine size={12} strokeWidth={2} />, loc: <Building2 size={12} strokeWidth={2} />, ur: <Shield size={12} strokeWidth={2} />, dx: <ClipboardList size={12} strokeWidth={2} /> };
  const sevColor = (s) => s === 'blocking' ? '#b30000' : s === 'high' ? '#8d6e00' : T.inkSoft;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: (snapshot && window.innerWidth >= 980) ? '1.15fr 1fr' : '1fr', gap: 14, alignItems: 'stretch' }}>
        {tiles.length > 0 && (<div>
          <div style={{ fontSize: 11.5, fontWeight: 800, color: T.inkSoft, marginBottom: 7 }}>CARE TODAY · What's due for this patient</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{tiles}</div>
        </div>)}
        {snapshot}
      </div>
      {brief.length > 0 && (
        <div style={{ ...card, marginTop: 12, padding: '9px 14px', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: '#0878E8' }}>PATIENT BRIEF</span>
          {brief.map((b, i) => (
            <span key={i} style={{ fontSize: 12, color: T.ink }}>
              {briefIcon[b.kind] || '•'} {b.label}
              <span style={{ color: T.inkSoft }}> · {fmtDate(b.at)}</span>
            </span>
          ))}
        </div>
      )}
      {(ruleChecks.length > 0 || atRisk.length > 0) && (() => {
        const overdueC = ruleChecks.filter(c => c.state === 'overdue');
        const dueSoonC = ruleChecks.filter(c => c.state !== 'overdue');
        const blockingF = atRisk.filter(f => f.severity === 'blocking');
        const otherF = atRisk.filter(f => f.severity !== 'blocking');
        const total = ruleChecks.length + atRisk.length;
        const pill = (bg, fg) => ({ background: bg, color: fg, borderRadius: 99, padding: '2px 10px', fontSize: 11, fontWeight: 700 });
        const stLabel = (c) => c.state === 'overdue' ? 'OVERDUE' : c.state === 'at_risk' ? 'AT RISK' : 'DUE SOON';
        const stColor = (c) => c.state === 'overdue' ? '#E5484D' : c.state === 'at_risk' ? '#E99A18' : '#0878E8';
        const when = (c) => c.hours_overdue != null ? `${c.hours_overdue}h overdue`
          : c.hours_remaining != null ? `${c.hours_remaining}h left`
          : c.due_at ? `due ${fmtDate(c.due_at)}` : '';
        const short = (f) => (f.rule_description || '').replace(/^Required:\s*/i, '');
        const row = (c, i2) => (
          <div key={'na' + i2} style={{ fontSize: 12.5, padding: '4px 0', display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span style={{ fontWeight: 700, color: stColor(c), fontSize: 10.5, minWidth: 62 }}>{stLabel(c)}</span>
            <span>{c.name}</span>
            <span style={{ color: T.inkSoft, fontSize: 11.5 }}>{when(c)}{c.detail ? ' \u00b7 ' + c.detail : ''}</span>
          </div>);
        return (
          <div style={{ ...card, marginTop: 12, padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11.5, fontWeight: 800, color: T.ink }}>NEEDS ATTENTION</span>
              {overdueC.length > 0 && <span style={pill('#FDECEE', '#E5484D')}>{overdueC.length} Overdue</span>}
              {dueSoonC.length > 0 && <span style={pill('#FFF6E5', '#E99A18')}>{dueSoonC.length} Due Soon</span>}
              {blockingF.length > 0 && <span style={pill('#FDECEE', '#E5484D')}>{blockingF.length} Blocking</span>}
              {otherF.length > 0 && <span style={pill('#F1EEFF', '#7C5CE7')}>{otherF.length} Findings</span>}
              {total > 0 && <button onClick={() => setShowAll(true)}
                style={{ marginLeft: 'auto', background: '#fff', color: '#0878E8', border: '1px solid #D8E3EE',
                  borderRadius: 8, padding: '5px 12px', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                View All {total} Issues {'\u2192'}</button>}
            </div>
            <div style={{ marginTop: 6 }}>
              {ruleChecks.slice(0, 3).map(row)}
              {blockingF.length > 0 && (
                <div style={{ fontSize: 12.5, padding: '4px 0', display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, color: '#E5484D', fontSize: 10.5, minWidth: 62 }}>BLOCKING</span>
                  <span>{blockingF.slice(0, 4).map(short).join(' \u00b7 ')}{blockingF.length > 4 ? ` +${blockingF.length - 4}` : ''}</span>
                  <button onClick={() => setShowAll(true)}
                    style={{ background: 'none', border: 'none', color: '#0878E8', fontWeight: 600,
                      fontSize: 12, cursor: 'pointer', padding: 0 }}>Review Findings {'\u2192'}</button>
                </div>)}
            </div>
          </div>);
      })()}
      {episodeId && <div style={{ marginTop: 12 }}><MissesPanel episodeId={episodeId} compact /></div>}
      {showAll && (() => {
        const overdueC = ruleChecks.filter(c => c.state === 'overdue');
        const dueC = ruleChecks.filter(c => c.state !== 'overdue');
        const blockingF = atRisk.filter(f => f.severity === 'blocking');
        const otherF = atRisk.filter(f => f.severity !== 'blocking');
        const gh = { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, margin: '12px 0 4px' };
        const it = { fontSize: 13, padding: '6px 0', borderBottom: '1px solid #F0F4F8' };
        const sub = { color: T.inkSoft, fontSize: 11.5, marginLeft: 8 };
        return (
          <div onClick={() => setShowAll(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,34,57,0.55)',
            zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12,
              width: 'min(680px, 96vw)', maxHeight: '84vh', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #D8E3EE',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>All Issues ({ruleChecks.length + atRisk.length})</strong>
                <button onClick={() => setShowAll(false)}
                  style={{ background: '#fff', border: '1px solid #D8E3EE', borderRadius: 8,
                    padding: '6px 12px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Close</button>
              </div>
              <div style={{ padding: '6px 20px 18px', overflowY: 'auto' }}>
                {overdueC.length > 0 && (<><div style={{ ...gh, color: '#E5484D' }}>Overdue</div>
                  {overdueC.map((c, i2) => <div key={'mo' + i2} style={it}>{c.name}
                    <span style={sub}>{(c.hours_overdue != null ? c.hours_overdue + 'h overdue' : '') + (c.detail ? ' \u00b7 ' + c.detail : '')}</span></div>)}</>)}
                {dueC.length > 0 && (<><div style={{ ...gh, color: '#E99A18' }}>Due Soon</div>
                  {dueC.map((c, i2) => <div key={'md' + i2} style={it}>{c.name}
                    <span style={sub}>{(c.hours_remaining != null ? c.hours_remaining + 'h left' : '') + (c.detail ? ' \u00b7 ' + c.detail : '')}</span></div>)}</>)}
                {blockingF.length > 0 && (<><div style={{ ...gh, color: '#E5484D' }}>Blocking Chart Findings</div>
                  {blockingF.map(f => <div key={'mb' + f.id} style={it}>{f.rule_description}
                    <span style={sub}>{(f.note_type || '') + ' \u00b7 ' + fmtDate(f.evaluated_at)}</span></div>)}</>)}
                {otherF.length > 0 && (<><div style={{ ...gh, color: '#7C5CE7' }}>Other Findings</div>
                  {otherF.map(f => <div key={'mf' + f.id} style={it}>{f.rule_description}
                    <span style={sub}>{(f.note_type || '') + ' \u00b7 ' + fmtDate(f.evaluated_at)}</span></div>)}</>)}
              </div>
            </div>
          </div>);
      })()}
    </div>
  );
}

const SCOPES = [['scope_presence', 'Presence'], ['scope_updates', 'Treatment updates'],
  ['scope_discharge', 'Discharge'], ['scope_billing', 'Billing'], ['scope_records', 'Records']];

function scopeList(r) {
  const on = SCOPES.filter(([k]) => r[k]).map(([, l]) => l);
  return on.length ? on.join(' · ') : 'Scope not specified';
}

export function ContactsReleases({ patientId, episodeId }) {
  const [data, setData] = useState(null);
  const [reloadN, setReloadN] = useState(0);
  const revokeRoi = async (id, who) => {
    const reason = window.prompt('Revoke the release for ' + who + '?\n\nAfter this, no treatment information may be shared with them. Enter how the client revoked it (for example: verbal request to staff, or written request):', '');
    if (reason === null) return;
    if (reason.trim().length < 3) { alert('Please enter how the client revoked it.'); return; }
    try { await axios.post(`${API}/api/emr/releases/${id}/revoke`, { reason: reason.trim() }); setReloadN(n => n + 1); }
    catch (e) { alert((e.response && e.response.data && e.response.data.error) || 'Could not revoke the release.'); }
  };
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState('');
  useEffect(() => {
    if (!patientId) return;
    let live = true;
    axios.get(`${API}/api/emr/overview-cards`, { params: { patientId, episodeId } })
      .then(r => { if (live) setData(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [patientId, episodeId, reloadN]);
  const saveContact = async (e) => {
    e.preventDefault(); setAddErr('');
    const f = e.target;
    try {
      await axios.post(`${API}/api/emr/patients/${patientId}/contacts`, {
        name: f.cname.value.trim(), relationship: f.crel.value.trim() || null,
        phone: f.cphone.value.trim() || null, is_emergency: f.cemerg.checked });
      setAdding(false); setReloadN(n => n + 1);
    } catch (err2) { setAddErr((err2.response && err2.response.data && err2.response.data.error) || 'Could not save'); }
  };
  const addForm = adding && (
    <form onSubmit={saveContact} style={{ marginTop: 10, padding: 12, border: `1px solid ${T.b100}`,
      borderRadius: 8, display: 'grid', gap: 8 }}>
      <input name="cname" placeholder="Name *" required style={{ font: 'inherit', fontSize: 13, padding: '6px 9px', borderRadius: 7, border: `1px solid ${T.b100}` }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <input name="crel" placeholder="Relationship" style={{ flex: 1, font: 'inherit', fontSize: 13, padding: '6px 9px', borderRadius: 7, border: `1px solid ${T.b100}` }} />
        <input name="cphone" placeholder="Phone" style={{ flex: 1, font: 'inherit', fontSize: 13, padding: '6px 9px', borderRadius: 7, border: `1px solid ${T.b100}` }} />
      </div>
      <label style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'center' }}>
        <input name="cemerg" type="checkbox" defaultChecked /> Emergency contact</label>
      {addErr && <div style={{ fontSize: 12, color: '#b30000' }}>{addErr}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => setAdding(false)} style={{ font: 'inherit', fontSize: 12, padding: '5px 11px', borderRadius: 7, border: `1px solid ${T.b100}`, background: '#fff', cursor: 'pointer' }}>Cancel</button>
        <button type="submit" style={{ font: 'inherit', fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 7, border: '1px solid #0878E8', background: '#0878E8', color: '#fff', cursor: 'pointer' }}>Save contact</button>
      </div>
    </form>
  );
  const addButton = (
    <button onClick={() => { setAdding(true); setAddErr(''); }} style={{ font: 'inherit', fontSize: 11.5,
      fontWeight: 600, color: '#0878E8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add contact</button>
  );
  if (!data) return null;
  const { contacts = [], releases_no_contact = [] } = data;
  if (!contacts.length && !releases_no_contact.length) return (
    <div style={{ ...card, gridColumn: '1 / -1' }}>
      <div style={{ padding: '13px 18px', borderBottom: `1px solid ${T.b100}` }}>
        <strong>Contacts / Emergency</strong>
      </div>
      <div style={{ padding: 18 }}>
        <span style={{ background: '#FFF6E5', color: '#E99A18', borderRadius: 99,
          padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>NO ACTIVE ROI</span>
        <div style={{ fontSize: 12.5, marginTop: 6, color: T.inkSoft }}>
          No contacts or releases on file. No treatment information may be disclosed to anyone.</div>
        <div style={{ marginTop: 8 }}>{addButton}</div>
        {addForm}
      </div>
    </div>
  );
  return (
    <div style={{ ...card, gridColumn: '1 / -1' }}>
      <div style={{ padding: '13px 18px', borderBottom: `1px solid ${T.b100}` }}>
        <strong>Contacts / Emergency</strong>
        <span style={{ fontSize: 11.5, color: T.inkSoft, marginLeft: 10 }}>Who to call · who staff may talk to</span>
        <span style={{ float: 'right' }}>{addButton}</span>
      </div>
      <div style={{ padding: 18 }}>
        {contacts.map(c => (
          <div key={c.id} style={{ padding: '10px 0', borderBottom: `1px solid ${T.b100}`,
            display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 210 }}>
              <div style={{ fontWeight: 700 }}>{c.name}
                {c.is_emergency && <span style={{ marginLeft: 8, background: '#fdecea', color: '#b30000',
                  borderRadius: 99, padding: '1px 9px', fontSize: 10.5, fontWeight: 700 }}>EMERGENCY</span>}</div>
              <div style={{ fontSize: 12.5, color: T.inkSoft }}>{c.relationship || '—'}{c.phone ? ` · ${c.phone}` : ''}</div>
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              {c.roi_id ? (
                <>
                  <span style={{ background: T.passBg, color: T.pass, borderRadius: 99,
                    padding: '1px 10px', fontSize: 11, fontWeight: 700 }}>ACTIVE ROI</span>
                  <div style={{ fontSize: 12.5, marginTop: 4 }}>May discuss: {scopeList(c)}</div>
                  {c.expiry_date && <div style={{ fontSize: 11.5, color: T.inkSoft }}>Expires {fmtDate(c.expiry_date)}</div>}
                  <div style={{ marginTop: 5 }}><button onClick={() => revokeRoi(c.roi_id, c.name)} style={{ padding: '2px 9px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid #E5484D', background: '#fff', color: '#E5484D', cursor: 'pointer' }}>Revoke release</button></div>
                </>
              ) : (
                <>
                  <span style={{ background: '#eceff1', color: '#546e7a', borderRadius: 99,
                    padding: '1px 10px', fontSize: 11, fontWeight: 700 }}>NO ROI</span>
                  <div style={{ fontSize: 12.5, marginTop: 4, color: '#b30000', fontWeight: 600 }}>
                    {c.is_emergency ? 'Emergency contact only — no treatment information may be disclosed' : 'No treatment information may be disclosed'}</div>
                </>
              )}
            </div>
          </div>
        ))}
        {addForm}
        {releases_no_contact.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: T.inkSoft, marginBottom: 5 }}>OTHER ACTIVE RELEASES</div>
            {releases_no_contact.map(r => (
              <div key={r.id} style={{ padding: '7px 0', borderBottom: `1px solid ${T.b100}`, fontSize: 12.5 }}>
                <strong>{r.recipient_name}</strong>{r.recipient_org ? ` · ${r.recipient_org}` : ''}{r.recipient_relationship ? ` · ${r.recipient_relationship}` : ''}{r.recipient_phone ? ` · ${r.recipient_phone}` : ''}
                <button onClick={() => revokeRoi(r.id, r.recipient_name)} style={{ float: 'right', padding: '2px 9px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid #E5484D', background: '#fff', color: '#E5484D', cursor: 'pointer' }}>Revoke release</button>
                <div style={{ color: T.inkSoft }}>May discuss: {scopeList(r)}{r.expiry_date ? ` · Expires ${fmtDate(r.expiry_date)}` : ''}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ===== P3 Slice 3: Clinical Snapshot + Treatment Team =====

const RISK_LABEL = { none: 'None', low: 'Low', moderate: 'Moderate', high: 'HIGH' };
const CODE_LABEL = { full_code: 'Full Code', dnr: 'DNR', dni: 'DNI', dnr_dni: 'DNR/DNI' };
const rowStyle = { display: 'flex', gap: 8, alignItems: 'baseline', padding: '5px 0', fontSize: 13 };
const kStyle = { color: T.inkSoft, minWidth: 150, fontSize: 12, fontWeight: 600 };

function SafetyRow({ k, v, tone }) {
  const colors = { bad: '#b30000', warn: '#8d6e00', ok: T.pass };
  return (
    <div style={rowStyle}>
      <span style={kStyle}>{k}</span>
      <span style={{ fontWeight: 600, color: colors[tone] || T.ink }}>{v}</span>
    </div>
  );
}

export function SafetySnapshot({ patientId, episodeId, safety: initialSafety, onGoTab, onSaved }) {
  const [safety, setSafety] = useState(initialSafety || null);
  const [round, setRound] = useState(null);
  const [medCount, setMedCount] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setSafety(initialSafety || null); }, [initialSafety]);
  useEffect(() => {
    if (!episodeId) return;
    let live = true;
    (async () => {
      const get = async (url, params) => { try { return (await axios.get(url, { params })).data; } catch { return null; } };
      const [r, meds] = await Promise.all([
        get(`${API}/api/rounds/board-episode`, { episodeId }),
        patientId ? get(`${API}/api/emr/patients/${patientId}/med-orders`, { scope_episode: episodeId }) : null,
      ]);
      if (!live) return;
      setRound(r && (r.data || r));
      if (Array.isArray(meds)) setMedCount(meds.filter(m => m.status === 'active').length);
    })();
    return () => { live = false; };
  }, [patientId, episodeId]);

  const openEdit = () => {
    setForm({
      suicide_risk: (safety && safety.suicide_risk) || '',
      fall_risk: safety ? safety.fall_risk : null,
      seizure_precautions: safety ? safety.seizure_precautions : null,
      withdrawal_monitoring: (safety && safety.withdrawal_monitoring) || '',
      diet: (safety && safety.diet) || '',
      code_status: (safety && safety.code_status) || '',
      medical_alerts: (safety && safety.medical_alerts) || '',
    });
    setErr(''); setEditing(true);
  };

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.put(`${API}/api/emr/safety-profile`, {
        episode_id: episodeId, patient_id: patientId,
        suicide_risk: form.suicide_risk || null,
        fall_risk: form.fall_risk,
        seizure_precautions: form.seizure_precautions,
        withdrawal_monitoring: form.withdrawal_monitoring || null,
        diet: form.diet || null,
        code_status: form.code_status || null,
        medical_alerts: form.medical_alerts || null,
      });
      setSafety(r.data.profile); setEditing(false);
      if (onSaved) onSaved(r.data.profile);
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || e.message); }
    setBusy(false);
  };

  const sel = { font: 'inherit', fontSize: 13, padding: '6px 8px', borderRadius: 8, border: `1px solid ${T.b100}`, width: '100%' };
  const yn = (v) => v === true ? 'Yes' : v === false ? 'No' : '\u2014';
  const sr = safety && safety.suicide_risk;

  return (
    <div style={card}>
      <div style={{ padding: '13px 18px', borderBottom: `1px solid ${T.b100}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>Clinical Snapshot</strong>
        <button style={{ font: 'inherit', fontSize: 12.5, fontWeight: 600, color: '#0878E8', background: 'none', border: 'none', cursor: 'pointer' }} onClick={openEdit}>Edit</button>
      </div>
      {!editing ? (
        <div style={{ padding: '10px 18px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
          <div>
            <SafetyRow k="Observation Level" v={(round && (round.level_code || (round.interval_minutes && `Q${round.interval_minutes}`))) || 'Not assigned'} tone={round && round.interval_minutes <= 30 ? 'warn' : undefined} />
            <SafetyRow k="Suicide Risk" v={sr ? RISK_LABEL[sr] : '\u2014'} tone={sr === 'high' ? 'bad' : sr === 'moderate' ? 'warn' : undefined} />
            <SafetyRow k="Fall Risk" v={yn(safety && safety.fall_risk)} tone={safety && safety.fall_risk ? 'warn' : undefined} />
            <SafetyRow k="Seizure Precautions" v={yn(safety && safety.seizure_precautions)} tone={safety && safety.seizure_precautions ? 'warn' : undefined} />
            <SafetyRow k="Diet" v={(safety && safety.diet) || 'Regular'} />
          </div>
          <div>
            <SafetyRow k="Medical Alerts" v={(safety && safety.medical_alerts) || 'None'} tone={safety && safety.medical_alerts ? 'bad' : undefined} />
            <SafetyRow k="Withdrawal Monitoring" v={(safety && safety.withdrawal_monitoring) || 'No'} tone={safety && safety.withdrawal_monitoring ? 'warn' : undefined} />
            <SafetyRow k="Code Status" v={CODE_LABEL[safety && safety.code_status] || 'Full Code'} />
            <div style={rowStyle}>
              <span style={kStyle}>Active Medications</span>
              <span style={{ fontWeight: 700, color: '#8d6e00' }}>{medCount != null ? medCount : '\u2014'}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: '#0878E8', cursor: 'pointer' }} onClick={() => onGoTab && onGoTab('mar')}>View MAR →</span>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: '12px 18px 16px' }}>
          {err && <div style={{ color: '#b30000', fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
            <label style={{ fontSize: 12 }}>Suicide risk
              <select style={sel} value={form.suicide_risk} onChange={e => setForm({ ...form, suicide_risk: e.target.value })}>
                <option value="">\u2014</option><option value="none">None</option><option value="low">Low</option>
                <option value="moderate">Moderate</option><option value="high">High</option>
              </select></label>
            <label style={{ fontSize: 12 }}>Code status
              <select style={sel} value={form.code_status} onChange={e => setForm({ ...form, code_status: e.target.value })}>
                <option value="">\u2014</option><option value="full_code">Full Code</option><option value="dnr">DNR</option>
                <option value="dni">DNI</option><option value="dnr_dni">DNR/DNI</option>
              </select></label>
            <label style={{ fontSize: 12 }}>Fall risk
              <select style={sel} value={form.fall_risk === true ? 'yes' : form.fall_risk === false ? 'no' : ''}
                onChange={e => setForm({ ...form, fall_risk: e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null })}>
                <option value="">\u2014</option><option value="yes">Yes</option><option value="no">No</option>
              </select></label>
            <label style={{ fontSize: 12 }}>Seizure precautions
              <select style={sel} value={form.seizure_precautions === true ? 'yes' : form.seizure_precautions === false ? 'no' : ''}
                onChange={e => setForm({ ...form, seizure_precautions: e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null })}>
                <option value="">\u2014</option><option value="yes">Yes</option><option value="no">No</option>
              </select></label>
            <label style={{ fontSize: 12 }}>Withdrawal monitoring
              <select style={sel} value={form.withdrawal_monitoring} onChange={e => setForm({ ...form, withdrawal_monitoring: e.target.value })}>
                <option value="">No</option><option value="CIWA">CIWA</option><option value="COWS">COWS</option>
              </select></label>
            <label style={{ fontSize: 12 }}>Diet
              <input style={sel} value={form.diet} placeholder="Regular" onChange={e => setForm({ ...form, diet: e.target.value })} /></label>
          </div>
          <label style={{ fontSize: 12, display: 'block', marginTop: 10 }}>Medical alerts
            <input style={sel} value={form.medical_alerts} placeholder="None" onChange={e => setForm({ ...form, medical_alerts: e.target.value })} /></label>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button disabled={busy} style={{ font: 'inherit', fontSize: 13, fontWeight: 600, padding: '7px 16px', borderRadius: 8, border: 'none', background: '#0878E8', color: '#fff', cursor: 'pointer' }} onClick={save}>{busy ? 'Saving\u2026' : 'Save'}</button>
            <button disabled={busy} style={{ font: 'inherit', fontSize: 13, padding: '7px 16px', borderRadius: 8, border: `1px solid ${T.b100}`, background: '#fff', cursor: 'pointer' }} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function TreatmentTeam({ staff, onMessageTeam }) {
  const members = [
    staff && staff.therapist && { name: staff.therapist, role: 'Primary Therapist' },
    staff && staff.case_manager && { name: staff.case_manager, role: 'Case Manager' },
    staff && staff.coordinator && { name: staff.coordinator, role: 'Coordinator' },
  ].filter(Boolean);
  return (
    <div style={card}>
      <div style={{ padding: '13px 18px', borderBottom: `1px solid ${T.b100}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>Treatment Team</strong>
        {onMessageTeam && <span style={{ fontSize: 12.5, fontWeight: 600, color: '#0878E8', cursor: 'pointer' }} onClick={onMessageTeam}>Message Team</span>}
      </div>
      <div style={{ padding: '10px 18px 14px', display: 'flex', gap: 22, flexWrap: 'wrap' }}>
        {members.length === 0 && <div style={{ color: T.inkSoft, fontSize: 13 }}>No team assignments recorded.</div>}
        {members.map((m, i) => (
          <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#e8eef5', color: '#22303E',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
              {m.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{m.name}</div>
              <div style={{ fontSize: 11.5, color: T.inkSoft }}>{m.role}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// Role emphasis config (spec §16) — data, not code. Unknown role = full default (most visible, never least).
export const EMPHASIS = {
  bht:               { hideCards: ['ur'], actions: ['Rounds', 'BHT / Vitals', 'MAR', 'New Note', 'Print Face Sheet'] },
  medical:           { rightOrder: 'team_first', actions: ['New Order', 'MAR', 'Labs', 'New Note'] },
  medical_admin:     { rightOrder: 'team_first', actions: ['New Order', 'MAR', 'Labs', 'New Note'] },
  therapist:         { actions: ['New Note', 'MAR', 'Records', 'Labs'] },
  case_manager:      { actions: ['New Note', 'MAR', 'Records', 'Labs'] },
  clinical_director: { actions: ['New Note', 'MAR', 'Records', 'Labs'] },
  ur_coordinator:    { urFirst: true, actions: ['Records', 'Labs'] },
};
export const emphasisFor = (role) => EMPHASIS[role] || {};

export function QuickActions({ onGoTab, patientId, patientName, role }) {
  const [printing, setPrinting] = useState(false);
  const printFaceSheet = async () => {
    if (!patientId || printing) return;
    setPrinting(true);
    try {
      const r = await axios.get(`${API}/api/emr/patients/${patientId}/casefile.pdf`,
        { params: { sections: 'face_sheet' }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${(patientName || 'patient').replace(/[^a-z0-9]+/gi, '_')}_face_sheet.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Face sheet download failed: ' + ((e.response && e.response.status) || e.message)); }
    setPrinting(false);
  };
  const actions = [
    ['New Note', Pencil, 'notes'],
    ['New Order', Stethoscope, 'drorders'],
    ['MAR', Pill, 'mar'],
    ['Rounds', Clock, 'bht'],
    ['BHT / Vitals', HeartPulse, 'bht'],
    ['Labs', FlaskConical, 'labs'],
    ['Records', FolderOpen, 'records'],
  ];
  const emph = emphasisFor(role);
  const visible = emph.actions ? actions.filter(([label]) => emph.actions.includes(label)) : actions;
  const showPrint = !emph.actions || emph.actions.includes('Print Face Sheet') || !['bht', 'ur_coordinator'].includes(role);
  return (
    <div style={card}>
      <div style={{ padding: '13px 18px 4px' }}><strong>Quick Actions</strong></div>
      <div style={{ padding: '8px 18px 16px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {visible.map(([label, Icon, tab]) => (
          <button key={label} onClick={() => onGoTab && onGoTab(tab)}
            style={{ font: 'inherit', fontSize: 12.5, fontWeight: 600, padding: '12px 16px', minWidth: 96,
              borderRadius: 10, border: `1px solid ${T.b100}`, background: '#fff', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, color: T.ink }}>
            <Icon size={18} strokeWidth={1.8} color="#4479B0" />{label}
          </button>
        ))}
        {showPrint && <button onClick={printFaceSheet} disabled={printing}
          style={{ font: 'inherit', fontSize: 12.5, fontWeight: 600, padding: '12px 16px', minWidth: 96,
            borderRadius: 10, border: `1px solid ${T.b100}`, background: '#fff', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, color: T.ink }}>
          <Printer size={18} strokeWidth={1.8} color="#4479B0" />{printing ? 'Generating\u2026' : 'Print Face Sheet'}
        </button>}
      </div>
    </div>
  );
}


export function UrAuthCard({ patientId, episodeId, role }) {
  const hidden = (emphasisFor(role).hideCards || []).includes('ur');
  const [ur, setUr] = useState(null);
  useEffect(() => {
    if (!episodeId) return;
    let live = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/emr/overview-cards`, { params: { patientId, episodeId } });
        if (live) setUr(r.data && r.data.ur ? r.data.ur : null);
      } catch { if (live) setUr(null); }
    })();
    return () => { live = false; };
  }, [patientId, episodeId]);

  if (hidden || !ur) return null;
  const d = ur.days_remaining;
  const tone = d != null && d < 0 ? '#b30000' : d != null && d <= 2 ? '#8d6e00' : T.pass;
  return (
    <div style={card}>
      <div style={{ padding: '13px 18px', borderBottom: `1px solid ${T.b100}` }}>
        <strong>Utilization Review / Authorization</strong>
      </div>
      <div style={{ padding: '12px 18px 16px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 17, fontWeight: 800 }}>{ur.loc || '\u2014'}</span>
          {ur.status && <span style={{ background: '#e6f4ea', color: T.pass, borderRadius: 99,
            padding: '2px 10px', fontSize: 11.5, fontWeight: 700 }}>{ur.status}</span>}
        </div>
        <div style={{ fontSize: 13, color: T.inkSoft, marginTop: 6 }}>
          {ur.date_from ? `Authorized ${fmtDate(ur.date_from)}` : ''}{ur.date_to ? ` \u2192 ${fmtDate(ur.date_to)}` : ''}
        </div>
        {d != null && (
          <div style={{ fontSize: 13.5, fontWeight: 700, color: tone, marginTop: 6 }}>
            {d < 0 ? `Expired ${-d} day(s) ago` : `Next review in ${d} day(s)`}
          </div>
        )}
      </div>
    </div>
  );
}


export function RecentlyResulted({ patientId, onGoTab }) {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    if (!patientId) return;
    let live = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/emr/labs/orders`, { params: { patient_id: patientId } });
        if (live) setOrders((r.data && r.data.orders) || []);
      } catch { if (live) setOrders([]); }
    })();
    return () => { live = false; };
  }, [patientId]);

  if (!orders || !orders.length) return null;
  const shown = orders.slice(0, 3);
  const fmtDT2 = (v) => v ? new Date(v).toLocaleString('en-US',
    { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit' }) : null;
  return (
    <div style={card}>
      <div style={{ padding: '13px 18px', borderBottom: `1px solid ${T.b100}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>Recently Resulted</strong>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: '#0878E8', cursor: 'pointer' }}
          onClick={() => onGoTab && onGoTab('labs')}>View All</span>
      </div>
      <div style={{ padding: '6px 18px 12px' }}>
        {shown.map((o, i) => (
          <div key={o.id || i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0',
            borderTop: i ? `1px solid ${T.b100}` : 'none', fontSize: 13, flexWrap: 'wrap' }}>
            <strong>{o.panel_name || o.lab_name || 'Lab order'}</strong>
            <span style={{ color: T.inkSoft, fontSize: 12 }}>
              {o.collected_at ? `Collected ${fmtDT2(o.collected_at)}` : `Ordered ${fmtDT2(o.created_at)}`}
              {o.resulted_at ? ` \u00b7 Resulted ${fmtDT2(o.resulted_at)}` : ''}
            </span>
            <span style={{ flex: 1 }} />
            {o.reviewed_by_name
              ? <span style={{ background: '#e6f4ea', color: T.pass, borderRadius: 99, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>Reviewed</span>
              : <span style={{ background: '#eef2f7', color: T.inkSoft, borderRadius: 99, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>{(o.status || '').replace(/_/g, ' ') || '\u2014'}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Insurance Information (Facesheet P4): payer + detail + VOB popup ──
export function InsuranceCard({ patient, epDetails }) {
  const [vob, setVob] = React.useState(null);
  const [showVob, setShowVob] = React.useState(false);
  React.useEffect(() => {
    let live = true;
    axios.get(`${API}/api/emr/patients/${patient.id}/vob`)
      .then(r => { if (live) setVob(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [patient.id]);
  const row = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 };
  const lb = { fontSize: 11, fontWeight: 700, color: T.inkSoft, textTransform: 'uppercase', letterSpacing: 0.3 };
  const d = (v) => v ? new Date(v).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '\u2014';
  const termed = patient.insurance_termination_date && new Date(patient.insurance_termination_date) < new Date();
  const b = (vob && vob.benefits) || {};
  return (
    <div style={{ background: '#fff', border: `1px solid ${T.b100}`, borderRadius: 12 }}>
      <div style={{ padding: '13px 18px', borderBottom: `1px solid ${T.b100}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>Insurance Information</strong>
        {patient.primary_payer && (
          <span style={{ background: termed ? '#FDECEE' : '#EAF8F1', color: termed ? '#E5484D' : '#16A36A',
            borderRadius: 99, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
            {termed ? 'Termed' : 'Active'}</span>)}
      </div>
      <div style={{ padding: 18, display: 'grid', gap: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{patient.primary_payer || '\u2014'}</div>
        {b.plan_name ? <div style={{ fontSize: 12.5, color: T.inkSoft, marginTop: -6 }}>{b.plan_name}</div> : null}
        <div style={row}>
          <div><div style={lb}>Member ID</div><div style={{ fontFamily: 'monospace' }}>{patient.member_id || '\u2014'}</div></div>
          <div><div style={lb}>Plan type</div><div>{patient.plan_type || '\u2014'}</div></div>
          <div><div style={lb}>Group #</div><div style={{ fontFamily: 'monospace' }}>{patient.group_number || '\u2014'}</div></div>
          <div><div style={lb}>Effective</div><div>{d(patient.insurance_effective_date)}</div></div>
          <div><div style={lb}>Termination</div><div>{d(patient.insurance_termination_date)}</div></div>
          <div><div style={lb}>Insurance phone</div><div>{patient.insurance_phone || '\u2014'}</div></div>
        </div>
        {/^no$/i.test(String(b.cob_completed || '').trim()) ? (
          <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, color: '#92400E' }}>
            <strong>Coordination of benefits is not complete.</strong> The plan may hold or deny claims until the member updates it.
            Have the patient call the plan{patient.insurance_phone ? ` at ${patient.insurance_phone}` : ''}, then set "Has COB been completed" to Yes on the verification.
          </div>
        ) : null}
        {(epDetails || (vob && vob.has_vob)) && (() => {
          const ed = epDetails || {};
          const at = ed.auth_thru || b.auth_thru;
          const daysLeft = at ? Math.ceil((new Date(at) - new Date()) / 86400000) : null;
          const expired = daysLeft !== null && daysLeft < 0;
          const vobWhen = b.verified_at ? new Date(b.verified_at).toLocaleDateString('en-US', { timeZone: 'UTC' }) : null;
          return (
            <div style={{ background: expired ? '#FDECEE' : '#FFF6E5', borderRadius: 8, padding: '10px 12px',
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12.5 }}>
              <div><div style={lb}>Auth through</div>
                <div style={{ fontWeight: 700, color: expired ? '#E5484D' : undefined }}>
                  {expired ? 'AUTH EXPIRED' : at ? `${d(at)}${daysLeft !== null ? ` \u00b7 ${daysLeft}d left` : ''}` : '\u2014'}</div></div>
              <div><div style={lb}>Next UR due</div><div>{ed.ur_due ? d(ed.ur_due) : '\u2014'}</div></div>
              <div><div style={lb}>UR status</div><div>{ed.ur_status || '\u2014'}</div></div>
              <div><div style={lb}>Auth #</div><div style={{ fontFamily: 'monospace' }}>{ed.auth_num || b.auth_number || '\u2014'}</div></div>
              <div><div style={lb}>Auth required</div><div>{b.auth_required === true ? 'Yes' : b.auth_required === false ? 'No' : '\u2014'}</div></div>
              <div><div style={lb}>VOB status</div><div style={{ fontWeight: 700, color: '#16A36A' }}>
                {vob && vob.pdf_kind === 'full' ? 'Full VOB' : vob && vob.pdf_kind === 'prelim' ? 'Prelim VOB' : vob && vob.pdf_kind === 'uploaded_doc' ? 'Doc on file' : vob && vob.has_vob ? 'Benefits recorded' : '\u2014'}{vobWhen ? ` \u00b7 ${vobWhen}` : ''}</div></div>
            </div>);
        })()}
        {vob && !vob.has_vob && (
          <div style={{ fontSize: 12.5, color: T.inkSoft }}>No VOB on file for this patient.</div>)}
        {vob && vob.pdf_url && (
          <button onClick={() => setShowVob(true)}
            style={{ background: '#4479B0', color: '#fff', border: 'none', borderRadius: 8,
              padding: '9px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer', justifySelf: 'start' }}>
            View VOB</button>)}
      </div>
      {showVob && (
        <div onClick={() => setShowVob(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,34,57,0.55)',
          zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12,
            width: 'min(880px, 96vw)', height: 'min(86vh, 900px)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.b100}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>VOB {vob.pdf_kind === 'full' ? '(Full)' : vob.pdf_kind === 'prelim' ? '(Preliminary)' : ''}</strong>
              <button onClick={() => setShowVob(false)}
                style={{ background: '#fff', border: `1px solid ${T.b200}`, borderRadius: 8,
                  padding: '6px 12px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Close</button>
            </div>
            {(b.deductible !== null && b.deductible !== undefined) || b.plan_name ? (
              <div style={{ padding: '8px 18px', borderBottom: `1px solid ${T.b100}`, fontSize: 12.5,
                display: 'flex', gap: 18, flexWrap: 'wrap', color: T.inkSoft }}>
                {b.plan_name && <span><b>Plan:</b> {b.plan_name}</span>}
                {b.deductible !== null && b.deductible !== undefined && <span><b>Deductible:</b> ${Number(b.deductible).toLocaleString()} {b.deductible_met !== null && b.deductible_met !== undefined ? `(met $${Number(b.deductible_met).toLocaleString()})` : ''}</span>}
                {b.oop_max !== null && b.oop_max !== undefined && <span><b>OOP max:</b> ${Number(b.oop_max).toLocaleString()} {b.oop_met !== null && b.oop_met !== undefined ? `(met $${Number(b.oop_met).toLocaleString()})` : ''}</span>}
                {b.oon_coverage_pct !== null && b.oon_coverage_pct !== undefined && <span><b>OON:</b> {b.oon_coverage_pct}%</span>}
              </div>) : null}
            <iframe title="vob" src={vob.pdf_url} style={{ flex: 1, border: 'none', borderRadius: '0 0 12px 12px' }} />
          </div>
        </div>)}
    </div>
  );
}


// ── Administrative (Facesheet P4): legacy face-sheet extras + validation state ──
export function AdministrativeCard({ patient, canValidate, onValidated }) {
  const [busy, setBusy] = React.useState(false);
  const lb = { fontSize: 11, fontWeight: 700, color: T.inkSoft, textTransform: 'uppercase', letterSpacing: 0.3 };
  const rowS = { display: 'grid', gridTemplateColumns: '130px 1fr', gap: 8, fontSize: 13, padding: '5px 0' };
  const addr = [patient.address, patient.city, patient.state, patient.zip].filter(Boolean).join(', ');
  const MS = { single: 'Single', married: 'Married', divorced: 'Divorced', separated: 'Separated', widowed: 'Widowed', partnered: 'Partnered' };
  const validate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/emr/patients/${patient.id}/validate-info`);
      if (onValidated) onValidated();
    } catch (e) { alert((e.response && e.response.data && e.response.data.error) || 'Validation failed'); }
    setBusy(false);
  };
  return (
    <div style={{ background: '#fff', border: `1px solid ${T.b100}`, borderRadius: 12 }}>
      <div style={{ padding: '13px 18px', borderBottom: `1px solid ${T.b100}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>Administrative</strong>
        {patient.info_validated ? (
          <span style={{ background: '#EAF8F1', color: '#16A36A', borderRadius: 99,
            padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>{'\u2713'} Validated</span>
        ) : (
          <span style={{ background: '#FFF6E5', color: '#E99A18', borderRadius: 99,
            padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>Needs Validation</span>
        )}
      </div>
      <div style={{ padding: '12px 18px' }}>
        <div style={rowS}><div style={lb}>Address</div><div>{addr || '\u2014'}</div></div>
        <div style={rowS}><div style={lb}>Language</div><div>{patient.preferred_language || '\u2014'}</div></div>
        <div style={rowS}><div style={lb}>Marital status</div><div>{MS[patient.marital_status] || patient.marital_status || '\u2014'}</div></div>
        <div style={rowS}><div style={lb}>Pronouns</div><div>{patient.pronouns || '\u2014'}</div></div>
        {!patient.info_validated && canValidate && (
          <button disabled={busy} onClick={validate}
            style={{ marginTop: 8, background: '#fff', color: '#16A36A', border: '1px solid #16A36A',
              borderRadius: 8, padding: '7px 14px', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}>
            {busy ? 'Saving\u2026' : 'Mark Validated'}</button>)}
        {patient.info_validated && patient.validated_at && (
          <div style={{ fontSize: 11.5, color: T.inkSoft, marginTop: 4 }}>
            Validated {new Date(patient.validated_at).toLocaleDateString('en-US')}</div>)}
      </div>
    </div>
  );
}
